import {
  formatSpeakerDisplayLabel,
  formatVoiceLabel,
  getVoiceLabelInitials,
} from '@app/modules/diarization/speaker-utils';
import { CopyLinkAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/CopyLinkAtTimestampButton';
import { PlayAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/PlayAtTimestampButton';
import { StreamPopup } from '@app/modules/logger/screens/stream/components/popup/StreamPopup';
import { StreamPopupHeader } from '@app/modules/logger/screens/stream/components/popup/StreamPopupHeader';
import { PersonAvatar } from '@app/modules/persons/components/PersonAvatar';
import { usePerson } from '@app/modules/persons/hooks/usePerson';
import { DiarizationFormattedType } from '@app/modules/search/types';
import { Timestamp, toDateTimeStr, toTimestamp } from '@mi-front/core';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { DiarizationConfidenceBadge } from './DiarizationConfidenceBadge';

type DiarizationInfoProps = {
  diarization: NonNullable<DiarizationFormattedType>;
  /** Speaker color for the avatar fallback background. */
  color?: string;
};

/**
 * Hover popup for a diarization marker: speaker identity, time, confidence, and actions.
 * Uses StreamPopup chrome; header is a left icon + time (no category chip).
 */
export function DiarizationInfo({ diarization, color }: DiarizationInfoProps) {
  const { time, segment, voice_label, person_id, distance, label } = diarization;
  const person = usePerson(person_id);
  const hasPersonId = !!person_id;
  const timeStamp = (toTimestamp(time) + segment.start * 1000) as Timestamp;
  const avatarBg = color ?? 'grey.400';

  const fallbackLabel = formatSpeakerDisplayLabel(
    formatVoiceLabel(voice_label) || formatVoiceLabel(label) || '',
  );
  const displayName = person?.name ?? fallbackLabel;
  const profession = person?.profession?.trim() || undefined;

  return (
    <StreamPopup
      minWidth={280}
      maxWidth={360}
      header={
        <StreamPopupHeader icon={<RecordVoiceOverOutlinedIcon />} time={toDateTimeStr(timeStamp)} />
      }
      footer={
        <>
          <PlayAtTimestampButton time={timeStamp} />
          <CopyLinkAtTimestampButton time={timeStamp} />
        </>
      }
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {hasPersonId ? (
          <PersonAvatar
            person={person}
            fallbackName={displayName}
            size="large"
            sx={{
              bgcolor: avatarBg,
              flexShrink: 0,
              border: '2px solid',
              borderColor: avatarBg,
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: avatarBg,
              typography: 'body2',
              flexShrink: 0,
              border: '2px solid',
              borderColor: avatarBg,
              boxSizing: 'border-box',
            }}
          >
            {getVoiceLabelInitials(voice_label || label)}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {displayName}
          </Typography>
          {profession && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {profession}
            </Typography>
          )}
        </Box>
      </Stack>

      <DiarizationConfidenceBadge distance={distance} />
    </StreamPopup>
  );
}
